import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import Layout from '../components/Layout';
import { INDUSTRIES, COMPANY_TYPES, COMPANY_SIZES } from '../lib/constants';
import { Save, Upload, AlertCircle, CheckCircle } from 'lucide-react';

export default function CompanyProfile() {
  const { company, refreshCompany } = useAuth();
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({
    name: company?.name || '',
    industry: company?.industry || '',
    company_type: company?.company_type || '',
    company_size: company?.company_size || '',
    founded_year: company?.founded_year || '',
    website: company?.website || '',
    linkedin_url: company?.linkedin_url || '',
    description: company?.description || '',
    country: company?.country || '',
    city: company?.city || '',
    address: company?.address || '',
    contact_name: company?.contact_name || '',
    contact_phone: company?.contact_phone || '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await supabase.from('companies')
        .update({
          ...form,
          founded_year: form.founded_year
            ? parseInt(form.founded_year) : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', company.id);
      await refreshCompany();
      setMsg({ type: 'success', text: 'Profile saved!' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const path = `${company.id}/logo`;
      await supabase.storage.from('avatars').upload(
        path, file, { upsert: true });
      const url = supabase.storage.from('avatars')
        .getPublicUrl(path).data.publicUrl;
      const busted = `${url}?v=${Date.now()}`;
      await supabase.from('companies')
        .update({ logo_url: busted })
        .eq('id', company.id);
      await refreshCompany();
      setMsg({ type: 'success', text: 'Logo updated!' });
    } catch (err) {
      setMsg({ type: 'error', text: 'Logo upload failed' });
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{
            fontSize: 22, fontWeight: 800, color: '#1E293B',
          }}>Company Profile</h1>
          <p style={{
            color: '#94A3B8', marginTop: 4, fontSize: 14,
          }}>
            This information is shown to students
          </p>
        </div>

        {msg && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px', borderRadius: 10,
            marginBottom: 20, fontSize: 13,
            background: msg.type === 'success'
              ? '#F0FDF4' : '#FFF1F2',
            border: `1px solid ${msg.type === 'success'
              ? '#BBF7D0' : '#FECDD3'}`,
            color: msg.type === 'success'
              ? '#16A34A' : '#E11D48',
          }}>
            {msg.type === 'success'
              ? <CheckCircle size={16} />
              : <AlertCircle size={16} />}
            {msg.text}
          </div>
        )}

        <div style={{
          background: '#fff', borderRadius: 20,
          padding: 32, border: '1px solid #E2E8F0',
          boxShadow: '0 4px 24px rgba(79,142,247,0.06)',
          display: 'flex', flexDirection: 'column', gap: 18,
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{
              width: 72, height: 72, borderRadius: 16,
              background: 'linear-gradient(135deg,rgba(79,142,247,0.1),rgba(124,58,237,0.05))',
              border: '2px solid #E2E8F0',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {company?.logo_url ? (
                <img src={company.logo_url}
                  alt="logo"
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                  }} />
              ) : (
                <span style={{
                  fontSize: 28, fontWeight: 900,
                  color: '#4F8EF7',
                }}>
                  {company?.name?.[0]?.toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <label style={{
                display: 'inline-flex',
                alignItems: 'center', gap: 6,
                padding: '9px 16px',
                border: '1.5px solid #E2E8F0',
                borderRadius: 10, cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
                color: '#64748B', background: '#F8FAFF',
              }}>
                <Upload size={14} />
                Upload Logo
                <input type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e =>
                    uploadLogo(e.target.files?.[0])} />
              </label>
              <p style={{
                fontSize: 11, color: '#94A3B8',
                marginTop: 4,
              }}>PNG or JPG, recommended 200×200px</p>
            </div>
          </div>

          <div style={{ height: 1, background: '#F1F5F9' }} />

          <CPInput label="Company Name *"
            value={form.name}
            onChange={v => set('name', v)} />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr', gap: 14,
          }}>
            <CPSelect label="Industry"
              value={form.industry}
              onChange={v => set('industry', v)}
              options={INDUSTRIES} />
            <CPSelect label="Company Type"
              value={form.company_type}
              onChange={v => set('company_type', v)}
              options={COMPANY_TYPES} />
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr', gap: 14,
          }}>
            <CPSelect label="Company Size"
              value={form.company_size}
              onChange={v => set('company_size', v)}
              options={COMPANY_SIZES} />
            <CPInput label="Founded Year"
              value={form.founded_year}
              onChange={v => set('founded_year', v)}
              type="number" placeholder="2020" />
          </div>
          <CPTextarea
            label="Company Description"
            value={form.description}
            onChange={v => set('description', v)}
            rows={4} />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr', gap: 14,
          }}>
            <CPInput label="Country"
              value={form.country}
              onChange={v => set('country', v)} />
            <CPInput label="City"
              value={form.city}
              onChange={v => set('city', v)} />
          </div>
          <CPInput label="Address (optional)"
            value={form.address}
            onChange={v => set('address', v)} />
          <CPInput label="Website"
            value={form.website}
            onChange={v => set('website', v)}
            placeholder="https://yourcompany.com" />
          <CPInput label="LinkedIn"
            value={form.linkedin_url}
            onChange={v => set('linkedin_url', v)} />

          <div style={{ height: 1, background: '#F1F5F9' }} />

          <CPInput label="Contact Person Name"
            value={form.contact_name}
            onChange={v => set('contact_name', v)} />
          <CPInput label="Contact Phone"
            value={form.contact_phone}
            onChange={v => set('contact_phone', v)} />

          <button onClick={save} disabled={saving}
            style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8,
              padding: '13px',
              background: saving ? '#CBD5E1'
                : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
              border: 'none', borderRadius: 12,
              color: '#fff', fontWeight: 700,
              fontSize: 15, cursor: saving ? 'not-allowed' : 'pointer',
              boxShadow: saving ? 'none'
                : '0 4px 16px rgba(79,142,247,0.3)',
              marginTop: 4,
            }}>
            {saving ? (
              <div className="spinner"
                style={{ width: 18, height: 18, borderWidth: 2 }} />
            ) : (
              <><Save size={16} /> Save Changes</>
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
}

const lStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#475569', marginBottom: 6,
};
const iStyle = {
  width: '100%', padding: '11px 14px',
  border: '1.5px solid #E2E8F0', borderRadius: 10,
  fontSize: 14, color: '#1E293B', background: '#F8FAFF',
};

function CPInput({ label, value, onChange,
  placeholder = '', type = 'text' }) {
  return (
    <div>
      <label style={lStyle}>{label}</label>
      <input type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder} style={iStyle}
        onFocus={e => e.target.style.borderColor = '#4F8EF7'}
        onBlur={e => e.target.style.borderColor = '#E2E8F0'}
      />
    </div>
  );
}
function CPSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label style={lStyle}>{label}</label>
      <select value={value}
        onChange={e => onChange(e.target.value)}
        style={{ ...iStyle, cursor: 'pointer' }}
        onFocus={e => e.target.style.borderColor = '#4F8EF7'}
        onBlur={e => e.target.style.borderColor = '#E2E8F0'}
      >
        <option value="">Select...</option>
        {options.map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}
function CPTextarea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label style={lStyle}>{label}</label>
      <textarea value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        style={{ ...iStyle, resize: 'vertical', lineHeight: 1.6 }}
        onFocus={e => e.target.style.borderColor = '#4F8EF7'}
        onBlur={e => e.target.style.borderColor = '#E2E8F0'}
      />
    </div>
  );
}